/**
 * Session Analytics — client-side stats from activity log and history.
 *
 * @module gibbs/analytics
 */

import { getActivityLog, getFrequentActions } from './activityLog';
import { getRecentEntries } from './conversationHistory';
import { listWorkflows } from './workflow/workflowLibrary';

/**
 * Compute session statistics from the activity log,
 * conversation history, and workflow library.
 *
 * @returns {Object} Session stats summary
 */
export function getSessionStats() {
    const log = getActivityLog();
    const history = getRecentEntries(50);
    const workflows = listWorkflows();

    const actionCount = log
        .filter((e) => e.type === 'action').length;
    const eventCount = log
        .filter((e) => e.type === 'event').length;
    const errorCount = log
        .filter((e) => e.type === 'error').length;

    // Time calculations
    const startTime = log.length > 0
        ? new Date(log[0].timestamp)
        : null;
    const endTime = log.length > 0
        ? new Date(log[log.length - 1].timestamp)
        : null;
    const durationMin = startTime && endTime
        ? Math.round((endTime - startTime) / 60000)
        : 0;

    return {
        totalActions: actionCount,
        totalEvents: eventCount,
        totalErrors: errorCount,
        sessionDuration: durationMin,
        conversationCount: history.length,
        savedWorkflows: workflows.length,
        topActions: getFrequentActions(1),
    };
}

/**
 * Format session stats as a Markdown table.
 *
 * @returns {string} Markdown string
 */
export function formatStatsAsMarkdown() {
    const stats = getSessionStats();
    const lines = [
        '## Session Analytics',
        '',
        '| Metric | Value |',
        '|--------|-------|',
        `| Actions executed | ${stats.totalActions} |`,
        `| Events recorded | ${stats.totalEvents} |`,
        `| Errors | ${stats.totalErrors} |`,
        `| Session duration | ${stats.sessionDuration} min |`,
        `| Conversations | ${stats.conversationCount} |`,
        `| Saved workflows | ${stats.savedWorkflows} |`,
        '',
    ];
    if (stats.topActions.length > 0) {
        lines.push('**Most used actions:**');
        for (const { action, count } of stats.topActions) {
            lines.push(`- ${action}: ${count}x`);
        }
    }
    return lines.join('\n');
}
